import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Lock, FileText, Globe } from "lucide-react";
import LoadingScreen from "./LoadingScreen";
import { toast } from "sonner";

/**
 * Public read-only view for a shared document.
 * Route: /share/doc/:token
 */
export default function DocShareView() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [doc, setDoc] = useState(null);
  const [accessing, setAccessing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: m } = await api.get(`/share/doc/${token}`);
        setMeta(m);
      } catch (e) {
        setError(e?.response?.data?.detail || "Share not found");
      }
      setLoading(false);
    })();
  }, [token]);

  useEffect(() => {
    if (!meta) return;
    if (meta.mode === "public") access();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta?.mode]);

  const access = async () => {
    setAccessing(true);
    try {
      const { data: d } = await api.post(`/share/doc/${token}/access`, { password: password || null });
      setDoc(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot access document");
    }
    setAccessing(false);
  };

  if (loading) return <LoadingScreen label="Opening document…" />;

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-[#EDEDED]">
      <div className="max-w-sm w-full text-center space-y-2 px-6">
        <FileText className="w-8 h-8 text-[#52525B] mx-auto" />
        <div className="text-xl font-display">Document unavailable</div>
        <div className="text-sm text-[#A1A1AA]">{error}</div>
        <button onClick={() => navigate("/")} className="mt-4 px-4 py-2 rounded bg-[#EDEDED] text-[#0A0A0A] text-xs font-semibold">Go home</button>
      </div>
    </div>
  );

  if (!meta) return null;

  // Invite/secured requires sign-in
  if ((meta.mode === "invite" || meta.mode === "secured") && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-[#EDEDED] px-6">
        <div className="max-w-sm w-full text-center space-y-3">
          <Lock className="w-6 h-6 text-[#5A90F5] mx-auto" />
          <div className="text-xl font-display">Sign in to view</div>
          <div className="text-sm text-[#A1A1AA]">
            <span className="text-[#EDEDED]">{meta.name}</span> is shared with specific people.
          </div>
          <button
            onClick={() => navigate(`/login?next=/share/doc/${token}`)}
            className="px-4 py-2 rounded bg-[#5A90F5] text-white text-xs font-semibold"
          >Sign in</button>
        </div>
      </div>
    );
  }

  // Secured: ask for password
  if (meta.mode === "secured" && !doc) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-[#EDEDED] px-6">
        <div className="max-w-sm w-full space-y-3">
          <Lock className="w-6 h-6 text-[#5A90F5] mx-auto" />
          <div className="text-xl font-display text-center">Enter password</div>
          <div className="text-sm text-[#A1A1AA] text-center">{meta.name} is password-protected.</div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") access(); }}
            placeholder="Password"
            className="w-full bg-[#141414] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#5A90F5] font-mono text-sm"
            autoFocus
          />
          <button
            onClick={access}
            disabled={accessing || !password}
            className="w-full px-4 py-2 rounded bg-[#5A90F5] text-white text-xs font-semibold disabled:opacity-60"
          >{accessing ? "Checking…" : "Open document"}</button>
        </div>
      </div>
    );
  }

  if (doc) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED]">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#2A2A2A]">
            <FileText className="w-5 h-5 text-[#5A90F5]" />
            <div className="text-2xl font-display">{doc.name || "Untitled"}</div>
            <span className="ml-auto text-[10px] uppercase tracking-widest text-[#A1A1AA] flex items-center gap-1">
              <Globe className="w-3 h-3" /> view-only
            </span>
          </div>
          <div
            className="prose prose-invert max-w-none text-sm leading-relaxed text-[#EDEDED]"
            dangerouslySetInnerHTML={{ __html: doc.body || "<p class='text-[#52525B] italic'>Empty document</p>" }}
          />
        </div>
      </div>
    );
  }

  return <LoadingScreen label="Opening document…" />;
}
